import { supabase } from "@supabaseClient/supabaseClient";

/**
 * Polymorphic image attachments for any org-scoped entity. Bytes live in the
 * private `entity-images` Storage bucket (org-scoped by RLS); this client owns
 * the metadata table + signed-URL resolution. Products are the first consumer.
 */
export type ImageEntityType = "product";

export interface EntityImage {
  id: string;
  organization_id: string;
  entity_type: string;
  entity_id: string;
  storage_path: string;
  thumbnail_path: string | null;
  content_type: string | null;
  size_bytes: number | null;
  width: number | null;
  height: number | null;
  sort_order: number;
  is_primary: boolean;
  created_at: string;
  created_by: string | null;
}

/** An image row with resolved, time-limited URLs for rendering. */
export interface EntityImageWithUrl extends EntityImage {
  /** Full display image. */
  url: string | null;
  /** Small thumbnail (falls back to the display URL when there's no variant). */
  thumbUrl: string | null;
}

const BUCKET = "entity-images";
const SIGNED_URL_TTL_SECONDS = 60 * 60; // 1 hour; matches the query staleTime.

/**
 * Max images per entity. Enforced in the UI and by a DB trigger
 * (enforce_entity_image_limit) — keep both in sync if this changes.
 */
export const MAX_ENTITY_IMAGES = 5;

export class EntityImagesClient {
  /** Images for an entity, primary first then by sort order. */
  static async list(
    entityType: ImageEntityType,
    entityId: string
  ): Promise<EntityImage[]> {
    const { data, error } = await supabase
      .from("entity_images")
      .select("*")
      .eq("entity_type", entityType)
      .eq("entity_id", entityId)
      .order("is_primary", { ascending: false })
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });

    if (error) throw error;
    return (data ?? []) as EntityImage[];
  }

  /**
   * The primary image per entity for a set of entity ids, keyed by entity id.
   * One query for the whole set — used by grid/thumbnail views so they don't
   * fan out to a per-entity request each.
   */
  static async listPrimaryForEntities(
    entityType: ImageEntityType,
    entityIds: string[]
  ): Promise<Record<string, EntityImage>> {
    if (entityIds.length === 0) return {};
    const { data, error } = await supabase
      .from("entity_images")
      .select("*")
      .eq("entity_type", entityType)
      .eq("is_primary", true)
      .in("entity_id", entityIds);

    if (error) throw error;
    const map: Record<string, EntityImage> = {};
    for (const row of (data ?? []) as EntityImage[]) {
      map[row.entity_id] = row;
    }
    return map;
  }

  /** Batch-resolve signed URLs keyed by storage path. */
  static async signedUrls(paths: string[]): Promise<Record<string, string>> {
    if (paths.length === 0) return {};
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrls(paths, SIGNED_URL_TTL_SECONDS);
    if (error) throw error;

    const map: Record<string, string> = {};
    for (const item of data ?? []) {
      if (item.path && item.signedUrl) map[item.path] = item.signedUrl;
    }
    return map;
  }

  /**
   * Upload a file to Storage and record its metadata row. If the row insert
   * fails the just-uploaded object is removed, so we never orphan bytes.
   */
  static async upload(params: {
    organizationId: string;
    entityType: ImageEntityType;
    entityId: string;
    file: File;
    thumbnail?: File | null;
    width?: number;
    height?: number;
    isPrimary?: boolean;
    sortOrder?: number;
  }): Promise<EntityImage> {
    const { organizationId, entityType, entityId, file, thumbnail } = params;
    const dir = `${organizationId}/${entityType}/${entityId}`;
    const id = crypto.randomUUID();
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `${dir}/${id}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(path, file, { contentType: file.type, upsert: false });
    if (uploadError) throw uploadError;

    // The thumbnail is an optimization, not a requirement: if it fails to
    // upload we keep the display image and fall back to it at read time.
    let thumbnailPath: string | null = null;
    if (thumbnail) {
      const thumbExt = thumbnail.name.split(".").pop()?.toLowerCase() || "jpg";
      const thumbPath = `${dir}/${id}-thumb.${thumbExt}`;
      const { error: thumbError } = await supabase.storage
        .from(BUCKET)
        .upload(thumbPath, thumbnail, {
          contentType: thumbnail.type,
          upsert: false,
        });
      if (thumbError) {
        await supabase.storage.from(BUCKET).remove([thumbPath]); // clear partial
      } else {
        thumbnailPath = thumbPath;
      }
    }

    const { data, error } = await supabase
      .from("entity_images")
      .insert({
        organization_id: organizationId,
        entity_type: entityType,
        entity_id: entityId,
        storage_path: path,
        thumbnail_path: thumbnailPath,
        content_type: file.type,
        size_bytes: file.size,
        width: params.width ?? null,
        height: params.height ?? null,
        sort_order: params.sortOrder ?? 0,
        is_primary: params.isPrimary ?? false,
      })
      .select()
      .single();

    if (error || !data) {
      const orphans = thumbnailPath ? [path, thumbnailPath] : [path];
      await supabase.storage.from(BUCKET).remove(orphans);
      throw error ?? new Error("Failed to record image");
    }
    return data as EntityImage;
  }

  /**
   * Delete every image for an entity — rows and their Storage objects (display
   * + thumbnail). The image table is polymorphic, so there's no FK cascade:
   * callers that delete the owning entity (product delete, bulk delete) must
   * invoke this or the bytes orphan.
   */
  static async removeAllForEntity(
    entityType: ImageEntityType,
    entityId: string
  ): Promise<void> {
    const { data, error } = await supabase
      .from("entity_images")
      .select("id, storage_path, thumbnail_path")
      .eq("entity_type", entityType)
      .eq("entity_id", entityId);
    if (error) throw error;

    const rows = (data ?? []) as Pick<
      EntityImage,
      "id" | "storage_path" | "thumbnail_path"
    >[];
    if (rows.length === 0) return;

    const { error: deleteError } = await supabase
      .from("entity_images")
      .delete()
      .in(
        "id",
        rows.map((r) => r.id)
      );
    if (deleteError) throw deleteError;

    const paths = rows.flatMap((r) =>
      r.thumbnail_path ? [r.storage_path, r.thumbnail_path] : [r.storage_path]
    );
    await supabase.storage.from(BUCKET).remove(paths);
  }

  /** Remove the metadata row (RLS-guarded) then the underlying objects. */
  static async remove(
    image: Pick<EntityImage, "id" | "storage_path" | "thumbnail_path">
  ): Promise<void> {
    const { error } = await supabase
      .from("entity_images")
      .delete()
      .eq("id", image.id);
    if (error) throw error;
    const paths = [image.storage_path];
    if (image.thumbnail_path) paths.push(image.thumbnail_path);
    await supabase.storage.from(BUCKET).remove(paths);
  }

  /**
   * Make `imageId` the primary. Clears the current primary first to respect the
   * one-primary-per-entity unique index (brief window with no primary is fine).
   */
  static async setPrimary(
    entityType: ImageEntityType,
    entityId: string,
    imageId: string
  ): Promise<void> {
    const { error: clearError } = await supabase
      .from("entity_images")
      .update({ is_primary: false })
      .eq("entity_type", entityType)
      .eq("entity_id", entityId)
      .eq("is_primary", true);
    if (clearError) throw clearError;

    const { error } = await supabase
      .from("entity_images")
      .update({ is_primary: true })
      .eq("id", imageId);
    if (error) throw error;
  }

  /** Persist a new ordering. */
  static async reorder(updates: { id: string; sort_order: number }[]): Promise<void> {
    for (const { id, sort_order } of updates) {
      const { error } = await supabase
        .from("entity_images")
        .update({ sort_order })
        .eq("id", id);
      if (error) throw error;
    }
  }
}
